import React, { useState, useEffect, useRef } from "react";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";
import Button from "./buttons";

export interface AppTourStep {
  target: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right" | "center";
  highlightElement?: boolean;
}

interface AppTourProps {
  isOpen: boolean;
  onClose: () => void;
  steps: AppTourStep[];
}

const AppTour: React.FC<AppTourProps> = ({ isOpen, onClose, steps }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [arrowDirection, setArrowDirection] = useState<
    "top" | "bottom" | "left" | "right" | "none"
  >("none");
  const [actualPosition, setActualPosition] = useState<
    "top" | "bottom" | "left" | "right" | "center"
  >("center");
  const [arrowOffset, setArrowOffset] = useState({
    horizontal: 0,
    vertical: 0,
  });
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayStep, setDisplayStep] = useState(0);
  const [isShaking, setIsShaking] = useState(false); // ✅ NEW: For shake animation
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && steps[currentStep]) {
      if (!isPositionCalculated) {
        requestAnimationFrame(() => {
          calculatePosition();
          setTimeout(() => {
            setIsPositionCalculated(true);
          }, 10);
        });
      } else {
        calculatePosition();
      }
    }

    if (isOpen) {
      document.documentElement.classList.add("app-tour-active");
      document.body.classList.add("app-tour-active");
    } else {
      document.documentElement.classList.remove("app-tour-active");
      document.body.classList.remove("app-tour-active");
      setIsPositionCalculated(false);
    }

    return () => {
      document.querySelectorAll(".app-tour-highlight").forEach((el) => {
        el.classList.remove("app-tour-highlight");
      });
      document.documentElement.classList.remove("app-tour-active");
      document.body.classList.remove("app-tour-active");
    };
  }, [currentStep, isOpen, steps]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, currentStep, steps]);

  // ✅ NEW: Handle clicks outside the tour tooltip
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node)
      ) {
        // Prevent the click from reaching other elements
        e.preventDefault();
        e.stopPropagation();

        // Trigger shake animation
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 820);
      }
    };

    // Use capture phase to catch clicks before they reach other elements
    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen]);

  const calculatePosition = () => {
    const targetElement = document.querySelector(steps[currentStep].target);
    if (!targetElement) return;

    document.querySelectorAll(".app-tour-highlight").forEach((el) => {
      el.classList.remove("app-tour-highlight");
    });

    const targetRect = targetElement.getBoundingClientRect();
    let preferredPosition = steps[currentStep].position;

    if (steps[currentStep].target === '[data-tour="screenshot-tool"]') {
      preferredPosition = "right";
    }

    const isViewListStep =
      steps[currentStep].target === '[data-tour="view-list"]';

    const tooltipWidth = 400;
    const tooltipHeight = 180;
    const gap = 16;
    const edgePadding = 0;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceTop = targetRect.top;
    const spaceBottom = viewportHeight - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewportWidth - targetRect.right;

    let finalPosition = preferredPosition;
    let top = 0;
    let left = 0;
    let arrow: "top" | "bottom" | "left" | "right" | "none" = "none";

    const canFitTop = spaceTop >= tooltipHeight + gap + edgePadding;
    const canFitBottom = spaceBottom >= tooltipHeight + gap + edgePadding;
    const canFitLeft = spaceLeft >= tooltipWidth + gap + edgePadding;
    const canFitRight = spaceRight >= tooltipWidth + gap + edgePadding;

    if (preferredPosition === "center") {
      top = viewportHeight / 2;
      left = viewportWidth / 2;
      arrow = "none";
      finalPosition = "center";
    } else if (preferredPosition === "top" && canFitTop) {
      top = targetRect.top - gap;
      left = targetRect.left + targetRect.width / 2;
      arrow = "bottom";
      finalPosition = "top";
    } else if (preferredPosition === "bottom" && canFitBottom) {
      top = targetRect.bottom + gap;
      left = targetRect.left + targetRect.width / 2;
      arrow = "top";
      finalPosition = "bottom";
    } else if (preferredPosition === "left" && canFitLeft) {
      top = targetRect.top + targetRect.height / 2;
      left = targetRect.left - gap;
      arrow = "right";
      finalPosition = "left";
    } else if (preferredPosition === "right" && canFitRight) {
      top = targetRect.top + targetRect.height / 2;
      left = targetRect.right + gap;
      arrow = "left";
      finalPosition = "right";
    } else {
      if (canFitBottom) {
        top = targetRect.bottom + gap;
        left = targetRect.left + targetRect.width / 2;
        arrow = "top";
        finalPosition = "bottom";
      } else if (canFitTop) {
        top = targetRect.top - gap;
        left = targetRect.left + targetRect.width / 2;
        arrow = "bottom";
        finalPosition = "top";
      } else if (canFitRight) {
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.right + gap;
        arrow = "left";
        finalPosition = "right";
      } else if (canFitLeft) {
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left - gap;
        arrow = "right";
        finalPosition = "left";
      } else {
        top = viewportHeight / 2;
        left = viewportWidth / 2;
        arrow = "none";
        finalPosition = "center";
      }
    }

    if (finalPosition === "top" || finalPosition === "bottom") {
      const halfWidth = tooltipWidth / 2;

      if (isViewListStep) {
        left = targetRect.right;
      } else if (left - halfWidth < edgePadding) {
        left = halfWidth + edgePadding;
      } else if (left + halfWidth > viewportWidth - edgePadding) {
        left = viewportWidth - halfWidth - edgePadding;
      }
    }

    if (finalPosition === "left" || finalPosition === "right") {
      const halfHeight = tooltipHeight / 2;
      const bottomPadding = 12;
      if (top - halfHeight < edgePadding) {
        top = halfHeight + edgePadding;
      } else if (top + halfHeight > viewportHeight - bottomPadding) {
        top = viewportHeight - halfHeight - bottomPadding;
      }
    }

    setTooltipPosition({ top, left });
    setArrowDirection(arrow);
    setActualPosition(finalPosition);

    const tooltipCenterX = left;
    const tooltipCenterY = top;
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    let horizontalOffset = 0;
    let verticalOffset = 0;

    if (finalPosition === "top" || finalPosition === "bottom") {
      if (isViewListStep) {
        const tooltipRightEdge = left;
        const arrowDefaultPosition = tooltipRightEdge - tooltipWidth / 2;
        horizontalOffset = targetCenterX - arrowDefaultPosition;
      } else {
        horizontalOffset = targetCenterX - tooltipCenterX;
      }
    } else if (finalPosition === "left" || finalPosition === "right") {
      verticalOffset = targetCenterY - tooltipCenterY;
    }

    setArrowOffset({ horizontal: horizontalOffset, vertical: verticalOffset });

    if (steps[currentStep].highlightElement) {
      targetElement.classList.add("app-tour-highlight");
    }
  };

  if (!isOpen) return null;

  const handleNext = () => {
    if (isTransitioning) return;

    if (currentStep < steps.length - 1) {
      setIsTransitioning(true);

      setCurrentStep(currentStep + 1);

      setTimeout(() => {
        setDisplayStep(currentStep + 1);
      }, 300);

      setTimeout(() => {
        setIsTransitioning(false);
      }, 600);
    } else {
      console.log("✅ Tour completed - calling onClose");
      onClose();
      setCurrentStep(0);
      setDisplayStep(0);
    }
  };

  const handleSkip = () => {
    if (isTransitioning) return;
    console.log("🚫 User skipped tour at step:", currentStep + 1);
    onClose();
    setCurrentStep(0);
    setDisplayStep(0);
  };

  const handleDone = () => {
    console.log("✅ User completed tour");
    onClose();
    setCurrentStep(0);
    setDisplayStep(0);
  };

  const currentStepData = steps[displayStep];
  const isLastStep = displayStep === steps.length - 1;

  const getTransform = () => {
    const isViewListStep =
      steps[currentStep]?.target === '[data-tour="view-list"]';

    switch (actualPosition) {
      case "top":
        return isViewListStep
          ? "translate(-100%, -100%)"
          : "translate(-50%, -100%)";
      case "bottom":
        return isViewListStep ? "translate(-100%, 0)" : "translate(-50%, 0)";
      case "left":
        return "translate(-100%, -50%)";
      case "right":
        return "translate(0, -50%)";
      case "center":
        return "translate(-50%, -50%)";
      default:
        return "translate(-50%, -50%)";
    }
  };

  const getShakeClass = () => {
    const isViewListStep =
      steps[currentStep]?.target === '[data-tour="view-list"]';

    if (!isShaking) return "";

    // Special handling for view-list step (uses translate(-100%, ...))
    if (isViewListStep) {
      return actualPosition === "top"
        ? "shake-animation-view-list-top"
        : actualPosition === "bottom"
          ? "shake-animation-view-list-bottom"
          : `shake-animation-${actualPosition}`;
    }

    return `shake-animation-${actualPosition}`;
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const isTablet =
    typeof window !== "undefined" &&
    window.innerWidth >= 640 &&
    window.innerWidth < 1024;

  return (
    <>
      {/* ✅ NEW: Overlay to block all interactions */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9998] bg-black bg-opacity-30"
          style={{ pointerEvents: "auto" }}
        />
      )}

      {isPositionCalculated && (
        <div
          ref={tooltipRef}
          className={`fixed z-[9999] bg-white rounded-xl ${getShakeClass()}`}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: getTransform(),
            maxWidth: isMobile
              ? "calc(100vw - 40px)"
              : isTablet
                ? "320px"
                : "340px",
            width: isMobile ? "calc(100vw - 40px)" : "340px",
            padding: isMobile ? "16px" : "20px",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            transition: isShaking
              ? "none"
              : "top 600ms cubic-bezier(0.4, 0, 0.2, 1), left 600ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {arrowDirection !== "none" && (
            <>
              {/* Arrow border layer */}
              <div
                className={`absolute ${
                  arrowDirection === "top"
                    ? "border-l-[11px] border-r-[11px] border-b-[11px] border-l-transparent border-r-transparent border-b-[rgba(0,0,0,0.08)]"
                    : arrowDirection === "bottom"
                      ? "border-l-[11px] border-r-[11px] border-t-[11px] border-l-transparent border-r-transparent border-t-[rgba(0,0,0,0.08)]"
                      : arrowDirection === "left"
                        ? "border-t-[11px] border-b-[11px] border-r-[11px] border-t-transparent border-b-transparent border-r-[rgba(0,0,0,0.08)]"
                        : arrowDirection === "right"
                          ? "border-t-[11px] border-b-[11px] border-l-[11px] border-t-transparent border-b-transparent border-l-[rgba(0,0,0,0.08)]"
                          : "hidden"
                }`}
                style={{
                  ...(arrowDirection === "top"
                    ? { top: "-11px" }
                    : arrowDirection === "bottom"
                      ? { bottom: "-11px" }
                      : arrowDirection === "left"
                        ? { left: "-11px" }
                        : { right: "-11px" }),
                  ...(arrowDirection === "top" || arrowDirection === "bottom"
                    ? {
                        left: "50%",
                        transform: `translateX(calc(-50% + ${arrowOffset.horizontal}px))`,
                      }
                    : {
                        top: "50%",
                        transform: `translateY(calc(-50% + ${arrowOffset.vertical}px))`,
                      }),
                }}
              />

              {/* Arrow main layer (white) */}
              <div
                className={`absolute ${
                  arrowDirection === "top"
                    ? "border-l-[10px] border-r-[10px] border-b-[10px] border-l-transparent border-r-transparent border-b-white -top-[10px]"
                    : arrowDirection === "bottom"
                      ? "border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-white -bottom-[10px]"
                      : arrowDirection === "left"
                        ? "border-t-[10px] border-b-[10px] border-r-[10px] border-t-transparent border-b-transparent border-r-white -left-[10px]"
                        : arrowDirection === "right"
                          ? "border-t-[10px] border-b-[10px] border-l-[10px] border-t-transparent border-b-transparent border-l-white -right-[10px]"
                          : "hidden"
                }`}
                style={{
                  ...(arrowDirection === "top" || arrowDirection === "bottom"
                    ? {
                        left: "50%",
                        transform: `translateX(calc(-50% + ${arrowOffset.horizontal}px))`,
                      }
                    : {
                        top: "50%",
                        transform: `translateY(calc(-50% + ${arrowOffset.vertical}px))`,
                      }),
                }}
              />
            </>
          )}

          <h3
            className={`font-semibold text-[#070707] mb-2 ${isMobile ? "text-sm" : "text-base"}`}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {currentStepData.title}
          </h3>
          <p
            className={`text-[#70707B] leading-relaxed ${isMobile ? "text-xs mb-3" : "text-sm mb-3"}`}
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            {currentStepData.description}
          </p>

          <div className={`flex items-center justify-between`}>
            <span
              className={`text-[#70707B] ${isMobile ? "text-xs" : "text-sm"}`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {isMobile
                ? `${displayStep + 1}/${steps.length}`
                : `Step ${displayStep + 1}/${steps.length}`}
            </span>
            <div
              className={`flex ${isMobile ? "gap-2" : "gap-3"}`}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  disabled={isTransitioning}
                  className={`text-[#070707] hover:text-gray-600 transition disabled:opacity-50 ${
                    isMobile ? "px-2 py-1 text-xs" : "px-3 py-1 text-sm"
                  }`}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Skip
                </button>
              )}
              <Button
                variant="primary"
                disabled={isTransitioning}
                className={isMobile ? "px-4 py-1 text-xs" : "px-5 py-1.5 text-sm"}
                onClick={handleNext}
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {isLastStep ? "Done" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        html.app-tour-active,
        body.app-tour-active {
          overflow: hidden !important;
          height: 100vh !important;
        }
        
        .app-tour-active #root {
          overflow: hidden !important;
          height: 100vh !important;
        }
        
        .app-tour-active main,
        .app-tour-active [class*="overflow-y-auto"],
        .app-tour-active [class*="overflow-auto"],
        .app-tour-active .custom-scrollbar {
          overflow: hidden !important;
        }
        
        .app-tour-active [class*="z-[9998]"],
        .app-tour-active [class*="z-[9999]"] {
          overflow: visible !important;
        }
        
        .app-tour-highlight {
          outline: 4px solid rgba(59, 130, 246, 0.6) !important;
          outline-offset: 4px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
          border-radius: 8px;
          z-index: 9997 !important;
          position: relative !important;
        }
        
        @media (max-width: 639px) {
          .app-tour-highlight {
            outline: 3px solid rgba(59, 130, 246, 0.6) !important;
            outline-offset: 3px !important;
          }
        }
        
        .app-tour-highlight * {
          z-index: inherit;
        }
        
        [data-tour="screenshot-tool"].app-tour-highlight {
          position: absolute !important;
        }

        /* ✅ NEW: Shake animations for different tooltip positions */
        @keyframes shake-center {
          0%, 100% {
            transform: translate(-50%, -50%) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-50%, -50%) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-50%, -50%) translateX(10px);
          }
        }

        @keyframes shake-top {
          0%, 100% {
            transform: translate(-50%, -100%) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-50%, -100%) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-50%, -100%) translateX(10px);
          }
        }

        @keyframes shake-bottom {
          0%, 100% {
            transform: translate(-50%, 0) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-50%, 0) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-50%, 0) translateX(10px);
          }
        }

        /* Special animations for view-list step (translate -100%) */
        @keyframes shake-view-list-top {
          0%, 100% {
            transform: translate(-100%, -100%) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-100%, -100%) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-100%, -100%) translateX(10px);
          }
        }

        @keyframes shake-view-list-bottom {
          0%, 100% {
            transform: translate(-100%, 0) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-100%, 0) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-100%, 0) translateX(10px);
          }
        }

        @keyframes shake-left {
          0%, 100% {
            transform: translate(-100%, -50%) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(-100%, -50%) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(-100%, -50%) translateX(10px);
          }
        }

        @keyframes shake-right {
          0%, 100% {
            transform: translate(0, -50%) translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate(0, -50%) translateX(-10px);
          }
          20%, 40%, 60%, 80% {
            transform: translate(0, -50%) translateX(10px);
          }
        }

        .shake-animation-center {
          animation: shake-center 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-top {
          animation: shake-top 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-bottom {
          animation: shake-bottom 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-left {
          animation: shake-left 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-right {
          animation: shake-right 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-view-list-top {
          animation: shake-view-list-top 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }

        .shake-animation-view-list-bottom {
          animation: shake-view-list-bottom 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>
    </>
  );
};

export default AppTour;
